import { DocStatusEnum, TDocType, TDocument, TOwnerCandidate } from '../types.ts';
import { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import useGetContragentTypes from '../../../hooks/useGetContragentTypes.ts';
import { getOwnerFullName, isSameId, mapDocName } from '../utils.ts';
import { DOCUMENT_TOOLTIPS, WEB_DOCS, WebDocName } from '~/constants/docs.ts';
import { useDocumentsQuery } from '~/queries/useDocumentsQuery.ts';
import { useSelector } from 'react-redux';
import {
  selectAdditionalInformationIsForeignFinancialUnderFATCA,
  selectControllingPersons,
  selectFatcaAdditionalInformation,
  selectGeneralInfoLegalResident,
  selectOwners,
} from '~/store/identification-form/selectors.ts';
import { TControllingPersons } from '~/modules/identification/forms/TaxResidencyAdditionalInformation/types.ts';
import { useFilterBeneficiaryOwners } from '~/modules/identification/forms/FatcaAdditionalInformation/hooks/useFilterBeneficiaryOwners.ts';
import { FatcaStatusNFFE } from '~/modules/identification/forms/FatcaAdditionalInformation/constants/constants.ts';
import { DOCUMENT_RELATED_ENTITY_TYPE } from '~/modules/identification/forms/Documents/constants.tsx';
import useGetUserRole from '~/hooks/useGetUserRole.ts';

export const useGetDocuments = (
  documentIds: number[],
  storingDocuments: TDocument[],
  informers: Partial<Record<WebDocName, boolean>> = {},
  isAssetManagerDocs?: boolean
) => {
  const { webDocs = [], personalDocs = [], isLoading } = useDocumentsQuery();
  const docTypes = useMemo(() => [...personalDocs, ...webDocs], [webDocs, personalDocs]);

  const { isAssetManager } = useGetUserRole();
  const { isLegalResident } = useGetContragentTypes();
  const { isControllingPersonUSTaxResident, isUSTaxResident } =
    useSelector(selectGeneralInfoLegalResident) || {};
  const { nffeInfo } = useSelector(selectFatcaAdditionalInformation);
  const isForeignFinancialUnderFATCAFop = useSelector(
    selectAdditionalInformationIsForeignFinancialUnderFATCA
  );

  const passiveNffeWithSubstantialUsOwners =
    isControllingPersonUSTaxResident &&
    nffeInfo?.fatcaStatusNFFE === FatcaStatusNFFE.PASSIVE_NFFE_WITH_SUBSTANTIAL_US_OWNERS;

  const { combinedOwners } = useFilterBeneficiaryOwners();
  const owners = useSelector(selectOwners);
  const beneficiaryOwners = useMemo(
    () => (owners || []).filter((owner) => owner.isBeneficiary),
    [owners]
  );

  const controllingPersons = useSelector(selectControllingPersons);
  const isTaxResidentOtherJurisdictionControllingPersons = useMemo(() => {
    if (!controllingPersons) return [];

    return controllingPersons.filter(
      (controllingPerson: TControllingPersons) => controllingPerson.isTaxResidentOtherJurisdiction
    );
  }, [controllingPersons]);

  const [documents, setDocuments] = useState<TDocument[]>([]);
  const informersKey = useMemo(() => JSON.stringify(informers), [informers]);

  const w9Id = WEB_DOCS[WebDocName.fatcaFormW9];
  const tinEinId = WEB_DOCS[WebDocName.fatcaUsTinCopy];
  const usOwnerDocIds = useMemo(() => [w9Id, tinEinId], [w9Id, tinEinId]);

  const documentSlots = useMemo(() => {
    return documentIds.flatMap((id, index) => {
      if (!usOwnerDocIds.includes(id)) return [{ id, withOwner: true }];
      if (index !== documentIds.indexOf(id)) return [];

      const includeCompanyDoc =
        id === tinEinId
          ? !passiveNffeWithSubstantialUsOwners
          : Boolean(isUSTaxResident || isForeignFinancialUnderFATCAFop);

      return [
        ...(passiveNffeWithSubstantialUsOwners
          ? combinedOwners.map(() => ({ id, withOwner: true }))
          : []),
        ...(includeCompanyDoc ? [{ id, withOwner: false }] : []),
      ];
    });
  }, [
    documentIds,
    usOwnerDocIds,
    tinEinId,
    passiveNffeWithSubstantialUsOwners,
    isUSTaxResident,
    isForeignFinancialUnderFATCAFop,
    combinedOwners,
  ]);

  const getWebDocName = (idDictWebDocument: number) =>
    Object.entries(WEB_DOCS).find(([, value]) => value === idDictWebDocument)?.[0] as
      WebDocName | undefined;

  const getTooltipText = (idDictWebDocument: number) => {
    const webDocName = getWebDocName(idDictWebDocument);
    return webDocName ? DOCUMENT_TOOLTIPS[webDocName] : undefined;
  };

  const getShowInformer = (idDictWebDocument: number) => {
    const webDocName = getWebDocName(idDictWebDocument);
    return webDocName ? Boolean(informers[webDocName]) : false;
  };

  const uboDocIds = useMemo(
    () => [
      WEB_DOCS[WebDocName.uboPassport],
      WEB_DOCS[WebDocName.uboTinCode],
      WEB_DOCS[WebDocName.crsControllingPersonTaxResidencyAssessment],
      ...(passiveNffeWithSubstantialUsOwners ? usOwnerDocIds : []),
    ],
    [passiveNffeWithSubstantialUsOwners, usOwnerDocIds]
  );

  const isUsOwnerDocument = (idDictWebDocument: number) =>
    usOwnerDocIds.includes(idDictWebDocument);

  const isCrsControllingPersonDocument = (idDictWebDocument: number) =>
    idDictWebDocument === WEB_DOCS[WebDocName.crsControllingPersonTaxResidencyAssessment];

  const getControllingPersonFullName = (person: TControllingPersons) =>
    [person.lastName, person.firstName].filter(Boolean).join(' ');

  const getOwnersSourceForDoc = (idDictWebDocument: number) =>
    isUsOwnerDocument(idDictWebDocument) ? combinedOwners : beneficiaryOwners;

  const withOwnerName = (name: string, label: string | null) =>
    label ? `${name} (${label})` : name;

  const getOwnerCandidates = (idDictWebDocument: number): TOwnerCandidate[] => {
    if (!uboDocIds.includes(idDictWebDocument)) return [];

    if (isCrsControllingPersonDocument(idDictWebDocument)) {
      return isTaxResidentOtherJurisdictionControllingPersons.map((person) => ({
        id: person.id,
        label: getControllingPersonFullName(person),
      }));
    }

    return getOwnersSourceForDoc(idDictWebDocument).map((owner) => ({
      id: owner.id,
      label: getOwnerFullName(owner),
    }));
  };

  const mapDocumentsWithNames = () => {
    const filteredDocTypes: TDocType[] = docTypes.filter((doc: TDocType) =>
      documentIds.includes(doc.id)
    );

    const remainingGroupedDocs: { [key: number]: TDocument[] } = {};
    storingDocuments.forEach((doc) => {
      if (!remainingGroupedDocs[doc.idDictWebDocument]) {
        remainingGroupedDocs[doc.idDictWebDocument] = [];
      }
      remainingGroupedDocs[doc.idDictWebDocument]?.push(doc);
    });

    const takeDoc = (idDictWebDocument: number, ownerId?: string): TDocument | undefined => {
      const docs = remainingGroupedDocs[idDictWebDocument] || [];
      let idx = -1;
      if (ownerId) {
        idx = docs.findIndex((doc) => isSameId(doc.relatedEntityId, ownerId));
      } else if (isAssetManager) {
        idx = docs.findIndex(
          (doc) => doc.relatedEntityType === DOCUMENT_RELATED_ENTITY_TYPE.REPRESENTATIVE
        );
        if (idx === -1) {
          idx = docs.findIndex((doc) => !doc.relatedEntityId);
        }
      } else {
        idx = docs.findIndex((doc) => isAssetManagerDocs || !doc.relatedEntityId);
      }

      if (idx === -1) return undefined;
      return docs.splice(idx, 1)[0];
    };

    const ownerSlotCounter = new Map<number, number>();

    return documentSlots.map(({ id, withOwner }) => {
      const docType = filteredDocTypes.find((type) => type.id === id);

      let owner: TOwnerCandidate | undefined;
      let realDoc: TDocument | undefined;

      if (withOwner) {
        const slotNumber = ownerSlotCounter.get(id) ?? 0;
        ownerSlotCounter.set(id, slotNumber + 1);
        owner = getOwnerCandidates(id)[slotNumber];

        if (owner) {
          realDoc = takeDoc(id, owner.id);
        }

        if (!realDoc && !isUsOwnerDocument(id)) {
          realDoc = takeDoc(id);
        }
      } else {
        realDoc = takeDoc(id);
      }

      const common = {
        id: uuidv4(),
        ownerId: owner?.id,
        tooltipText: getTooltipText(id),
        showInformer: getShowInformer(id),
      };

      if (realDoc) {
        return {
          ...realDoc,
          ...common,
          nameDoc: withOwnerName(
            mapDocName(isLegalResident, docType, realDoc),
            owner?.label ?? null
          ),
          documentName: realDoc.nameDoc,
        } as TDocument;
      }

      return {
        ...common,
        idDictWebDocument: id,
        nameDoc: withOwnerName(mapDocName(isLegalResident, docType), owner?.label ?? null),
        idDoc: 0,
        docStatus: [DocStatusEnum.NOT_VERIFIED],
      } as TDocument;
    });
  };

  useEffect(() => {
    !isLoading && setDocuments(mapDocumentsWithNames());
  }, [
    documentSlots,
    storingDocuments,
    docTypes,
    informersKey,
    isLoading,
    beneficiaryOwners,
    combinedOwners,
    controllingPersons,
    uboDocIds,
    isUSTaxResident,
    isForeignFinancialUnderFATCAFop,
    isAssetManager,
  ]);

  return { documents };
};
